import React from 'react';
import { ThemedStyledProps } from 'styled-components';

import {
  BorderRadius,
  ConfigurationFeatureFlag,
  TextWeight,
} from '@tink/config/src/tinkLinkConfig';
import {
  Button as CoreButton,
  ExtendedButtonProps as CoreButtonProps,
} from '@tink/core/src/components/atoms/Button/Button';
import { Spinner } from '@tink/core/src/components/atoms/Spinner/Spinner';
import { styled } from '../../../core/theme/styled';
import { useConfiguration } from '../../ApplicationProviders/ConfigurationProvider/ConfigurationProvider';
import { TinkLinkTheme } from '../../ApplicationProviders/ConfiguredThemeProvider/tinkLinkTheme';
import { PageView, UserInteractionEvent } from '../../../models/analytics';
import {
  InteractionElement,
  parentMessenger,
} from '../../../core/Analytics/ParentApplicationEventMessenger';
import { useAnalytics } from '../../../core/Analytics/AnalyticsProvider';

type ButtonTextProp = {
  hide?: boolean;
};

export type ButtonProps = {
  loadingSpinner?: boolean;
} & CoreButtonProps;

export type StyledButtonWithFeatureFlags = ButtonProps & {
  textTransformUppercase: boolean;
  noShadow: boolean;
};

/* istanbul ignore next: because we can't simulate this in tests */
const StyledButton = styled(({ theme, ...rest }: ThemedStyledProps<ButtonProps, TinkLinkTheme>) => (
  <CoreButton {...rest} />
))`
  && {
    font-weight: ${(props) =>
      props.theme.configuration.buttons.textWeight === TextWeight.BOLD
        ? props.theme.font.weights.semiBold
        : props.theme.font.weights.regular};
    border-radius: ${({
      theme: {
        configuration: {
          buttons: { borderRadius },
        },
        roundedCorners,
      },
    }) => {
      if (borderRadius === BorderRadius.ROUNDED) return roundedCorners.rounded;

      if (borderRadius === BorderRadius.SHARP) return roundedCorners.sharp;

      return roundedCorners.pill;
    }};
    width: ${(props) => (props.theme.configuration.buttons.fullWidth ? '100%' : 'auto')};
    margin-bottom: ${(props) => props.theme.sizes.normal};
    text-transform: ${(props) =>
      props.theme.configuration.buttons.textUppercase ? 'uppercase' : 'none'};

    :disabled {
      background-color: ${(props) => props.theme.configuration.buttonBackgroundColor}66;
      color: ${(props) => props.theme.configuration.buttonTextColor}66;
    }
  }
  ${(props) =>
    props.variant === 'contained' &&
    `
    && {
      box-shadow: ${
        props.theme.configuration.buttons.shadow ? '0 4px 12px 0 rgba(0, 0, 0, 0.12)' : 'none'
      };
      background-color: ${props.theme.configuration.buttonBackgroundColor};
      color: ${props.theme.configuration.buttonTextColor};
    }
  `}

  ${(props) =>
    props.size === 'large' &&
    `
    && {
      padding-left: 44px;
      padding-right: 44px;
      line-height: ${props.theme.sizes.normedium};
    }
  `}
`;

const StyledButtonWithFeatureFlags = styled(
  ({
    textTransformUppercase,
    theme,
    noShadow,
    ...rest
  }: ThemedStyledProps<StyledButtonWithFeatureFlags, TinkLinkTheme>) => <StyledButton {...rest} />
)`
  && {
    ${(props) => props.textTransformUppercase && 'text-transform: uppercase;'}
    ${(props) =>
      props.noShadow &&
      `
    && {
      box-shadow: unset;
      :hover {
        box-shadow: unset;
      }`}
      ${(props) =>
      props.noShadow &&
      props.variant === 'contained' &&
      `
    && {
      :hover {
        background-color: ${props.theme.configuration.buttonBackgroundColor};
      }`}
  }
`;

const StyledSpinner = styled(Spinner)`
  position: absolute;
  color: ${(props) => props.theme.configuration.buttonTextColor};
`;

const ButtonText = styled.span<ButtonTextProp>`
  ${(props) => props.hide && 'visibility: hidden;'};
`;

export const Button: React.FC<ButtonProps> = ({
  children,
  loadingSpinner = false,
  disabled = false,
  ...rest
}) => {
  const configuration = useConfiguration();
  const { featureFlags } = configuration.data;

  const textTransformUppercase = featureFlags.includes(
    ConfigurationFeatureFlag.UPPERCASE_BUTTON_TEXT
  );

  const noShadow = featureFlags.includes(ConfigurationFeatureFlag.NO_SHADOW_BUTTON);

  return (
    <StyledButtonWithFeatureFlags
      size="large"
      disabled={loadingSpinner || disabled}
      {...rest}
      textTransformUppercase={textTransformUppercase}
      noShadow={noShadow}
    >
      {loadingSpinner && <StyledSpinner size={25} color="inherit" />}
      <ButtonText hide={loadingSpinner}>{children}</ButtonText>
    </StyledButtonWithFeatureFlags>
  );
};

type PrimaryButtonProps = ButtonProps & {
  pageView: PageView;
};

export const PrimaryButton: React.FC<PrimaryButtonProps> = ({
  onClick,
  pageView,
  children,
  ...rest
}) => {
  const { tinklinkAnalytics } = useAnalytics();

  return (
    <Button
      onClick={(e) => {
        tinklinkAnalytics.sendInteractionEvent({
          action: `${pageView}/${UserInteractionEvent.PRIMARY_BUTTON}`,
          view: pageView,
        });

        parentMessenger.fireInteractionEvent({
          pageView,
          element: InteractionElement.PRIMARY_BUTTON,
        });

        if (onClick) {
          onClick(e);
        }
      }}
      {...rest}
    >
      {children}
    </Button>
  );
};
